import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";
import { loadTeacherWorkload } from "../../services/teacherWorkloadService";

function campusVariants(campus) {
  const c = String(campus || "").trim();
  const lc = c.toLowerCase();
  if (!lc) return [];
  if (lc === "gapan") return ["gapan", "Gapan", "gapan city", "Gapan City"];
  if (lc === "bulacan") return ["bulacan", "Bulacan"];
  return Array.from(new Set([lc, c]));
}
function norm(s) {
  return String(s || "").trim().toLowerCase();
}
function campusMatch(value, expectedCampus) {
  return campusVariants(expectedCampus).map(norm).includes(norm(value));
}
function sectionMatch(student, section) {
  const target = norm(section);
  const vals = [];
  const secArr = student?.sections;
  if (Array.isArray(secArr)) vals.push(...secArr.map(x => String(x || "")));
  else if (typeof secArr === "string") vals.push(...secArr.split(","));
  vals.push(String(student?.section || ""));
  return vals.some(v => norm(v) === target);
}

function splitSections(obj) {
  if (Array.isArray(obj)) return obj.map(s => String(s).trim()).filter(Boolean);
  if (obj && Array.isArray(obj.sections)) return obj.sections.map(s => String(s).trim()).filter(Boolean);
  return String(obj || "").split(",").map(s => s.trim()).filter(Boolean);
}

function formatName(s) {
  const last = String(s.lastName || "").trim();
  const first = String(s.firstName || "").trim();
  const middle = String(s.middleName || s.middleInitial || "").trim();
  const core = `${last}${last && first ? ", " : ""}${first}`;
  return middle ? `${core} ${middle}` : core;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function keyId(parts) {
  return parts.map(s => String(s || "").toLowerCase().trim().replace(/\s+/g, "_")).join("__");
}

export default function TeacherAttendance() {
  const user = getCurrentUser();
  const campus = String(user?.campus || "gapan").toLowerCase();
  const [schoolYear, setSchoolYear] = useState("");
  const [assignedSections, setAssignedSections] = useState([]);
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [date, setDate] = useState(today());
  const [students, setStudents] = useState([]);
  const [present, setPresent] = useState({});
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await loadTeacherWorkload({ user, campus });
      const wl = res.data || {};
      setSchoolYear(res.schoolYear || "");
      const adv = Array.isArray(wl.advisories) ? wl.advisories : [];
      const cls = Array.isArray(wl.classes) ? wl.classes : [];
      const all = [];
      adv.forEach(a => {
        const g = String(a.grade || "");
        const s = String(a.section || "");
        if (g && s) all.push({ grade: g, section: s, source: "advisory" });
      });
      cls.forEach(c => {
        const g = String(c.grade || "");
        const s = String(c.section || "");
        if (g && s) all.push({ grade: g, section: s, source: "class" });
      });
      const dedup = new Map();
      all.forEach(x => { dedup.set(`${x.grade}__${x.section}`, { grade: x.grade, section: x.section, source: x.source }); });
      const list = Array.from(dedup.values()).sort((a, b) => `${a.grade} ${a.section}`.localeCompare(`${b.grade} ${b.section}`));
      setAssignedSections(list);
      if (!grade && list.length) setGrade(String(list[0].grade || ""));
      if (!section && list.length) setSection(String(list[0].section || ""));
    })();
  }, [user?.uid, user?.email, campus]);

  const sectionOptions = useMemo(() => assignedSections.map(a => ({
    key: `${a.grade}__${a.section}`,
    label: `${a.grade} — ${a.section}${a.source ? ` (${a.source})` : ""}`,
    grade: String(a.grade || ""),
    section: String(a.section || "")
  })), [assignedSections]);

  useEffect(() => {
    (async () => {
      if (!campus || !grade || !section) { setStudents([]); return; }
      const base = collection(db, "students");
      const all = new Map();
      const snap = await getDocs(base);
      snap.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
      const allRows = Array.from(all.values());
      const afterCampus = allRows.filter(s => campusMatch(s.campus, campus));
      const afterGrade = afterCampus.filter(s => norm(s.gradeLevel) === norm(grade));
      const afterSection = afterGrade.filter(s => sectionMatch(s, section));
      const rows = afterSection;
      rows.sort((a, b) => formatName(a).localeCompare(formatName(b)));
      setStudents(rows);
      setPresent(Object.fromEntries(rows.map(r => [r.id, true])));
      setDebugInfo({
        filters: { campus, grade, section },
        counts: {
          total: allRows.length,
          campus: afterCampus.length,
          grade: afterGrade.length,
          section: afterSection.length
        }
      });
    })();
  }, [campus, grade, section, date]);

  function exportCSV() {
    const headers = ["ID", "Full Name", "Grade", "Section", "Date", "Present"];
    const rows = students.map(s => [
      s.id, formatName(s), s.gradeLevel || "", splitSections(s.section).join(" / "), date, present[s.id] ? "Yes" : "No"
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${campus}_${grade}_${section}_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function save() {
    if (!campus || !grade || !section) return;
    setSaving(true);
    setInfo("Saving attendance…");
    try {
      const id = keyId([campus, grade, section, date]);
      const ref = doc(db, "attendance", id);
      const payload = {
        campus,
        grade,
        section,
        date,
        schoolYear: schoolYear || "",
        recordedByUid: user?.uid || "",
        recordedByEmail: user?.email || "",
        recordedAt: new Date().toISOString(),
        students: students.map(s => ({ id: s.id, name: formatName(s), present: !!present[s.id] }))
      };
      await setDoc(ref, payload, { merge: true });
      setInfo("Attendance saved.");
    } catch {
      setInfo("Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Attendance</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Class / Advisory</label>
            <select
              className="form-select"
              value={`${grade}__${section}`}
              onChange={e => {
                const [g, s] = String(e.target.value).split("__");
                setGrade(g || "");
                setSection(s || "");
              }}
            >
              <option value="">Select</option>
              {sectionOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="col-md-5 d-flex gap-2">
            <button className="btn btn-primary" onClick={save} disabled={saving || !grade || !section}>Save</button>
            <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={students.length === 0}>Export CSV</button>
            <button className="btn btn-outline-primary" onClick={() => window.print()} disabled={students.length === 0}>Print</button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th style={{ width: 80 }}>Present</th><th>Name</th><th style={{ width: 160 }}>Grade</th><th style={{ width: 220 }}>Section</th></tr></thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id}>
                  <td>
                    <input type="checkbox" className="form-check-input" checked={!!present[s.id]} onChange={e => setPresent(prev => ({ ...prev, [s.id]: e.target.checked }))} />
                  </td>
                  <td>{formatName(s)}</td>
                  <td>{s.gradeLevel || ""}</td>
                  <td>{splitSections(s.section).join(", ")}</td>
                </tr>
              ))}
              {students.length === 0 && <tr><td colSpan="4" className="text-center text-muted">No students found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`@media print { @page { size: letter portrait; margin: 12mm; } .btn, .form-select, input[type=date] { display: none !important; } }`}</style>
      {info && <div className="alert alert-info mt-3">{info}</div>}
      {debugInfo && (
        <div className="card mt-3">
          <div className="card-body">
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Debug</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
